import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';

import { useApplicationState } from '@atlas/core';
import { NotificationTopic, TransportManager } from '@atlas/transport';

import { bonusStatuses, bonusTypes } from 'common/constants';
import useBonuses from 'src/common/Hooks/useBonuses';
import { sortBonusesByAvailabilityAndAmount } from 'src/common/utils/sortBonusesByAvailabilityAndAmount';

const { DEPOSIT, NO_DEPOSIT, RELOAD_DEPOSIT, RELOAD_NO_DEPOSIT } = bonusTypes;
const { ACTIVATED, IN_PROGRESS } = bonusStatuses;
const CASINO_PRODUCT_TYPE = 'CASINO';
const SPORTSBOOK_PRODUCT_TYPE = 'SPORTSBOOK';

const checkBonusTypeFits = bonusType => [DEPOSIT, RELOAD_DEPOSIT].includes(bonusType);
const getActivationSortValue = bonus => bonus?.createTime ?? bonus?.id ?? 0;
const normalizeBonusWithFinanceData = (bonus, currency) => {
  const normalizedBonus = bonus?.bonus ?? bonus;
  const financeData =
    normalizedBonus?.financeDataList?.find(item => item.currency === currency) ??
    bonus?.financeData;

  return {
    ...bonus,
    ...normalizedBonus,
    financeData,
    id: bonus?.id ?? normalizedBonus?.id,
  };
};

const isCasinoDeposit = bonus =>
  checkBonusTypeFits(bonus?.bonusType) && bonus?.productType === CASINO_PRODUCT_TYPE;
const orderBonusesByAvailability = bonusesList => {
  const bonusesBuckets = bonusesList.reduce(
    (accumulator, currentBonus) => {
      const bucketKey = currentBonus.disabled ? 'disabled' : 'enabled';

      accumulator[bucketKey].push(currentBonus);

      return accumulator;
    },
    { disabled: [], enabled: [] },
  );

  return [...bonusesBuckets.enabled, ...bonusesBuckets.disabled];
};

const QuickDepositBonusesContext = createContext({
  activatedBonus: null,
  amountToGetBonus: 0,
  bonusesList: [],
  depositBonuses: [],
  inputValue: '',
  intendedBonusRef: {
    current: null,
  },
  isBonusesDisabled: false,
  paymentMethod: null,
  selectedBonus: null,
  setInputValue: () => {},
  setPaymentMethod: () => {},
  setSelectedBonus: () => {},
  showBonus: true,
});

export const useQuickDepositBonusesContext = () => useContext(QuickDepositBonusesContext);

export const QuickDepositBonusesProvider = ({ children }) => {
  const { activeBonuses, bonuses } = useBonuses();
  const [selectedBonus, setSelectedBonus] = useState(null);
  const [isBonusesDisabled, setIsBonusesDisabled] = useState(false);
  const [depositCompleteBonus, setDepositCompleteBonus] = useState(null);
  const [depositCompletePaymentMethodId, setDepositCompletePaymentMethodId] = useState(null);
  const [
    hasPaymentMethodChangedSinceDepositComplete,
    setHasPaymentMethodChangedSinceDepositComplete,
  ] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState(null);
  const applicationState = useApplicationState();
  const { country, currency } = applicationState.account;
  const [depositAmount, setDepositAmount] = useState();
  const intendedBonusRef = useRef(null);
  const getMinDeposit = useCallback(
    bonus =>
      bonus?.financeData?.minDeposit ??
      bonus?.financeDataList?.find(item => item.currency === currency)?.minDeposit ??
      0,
    [currency],
  );
  const resetCompletedDepositState = useCallback(() => {
    setDepositCompleteBonus(null);
    setDepositCompletePaymentMethodId(null);
    setHasPaymentMethodChangedSinceDepositComplete(false);
  }, []);
  const applyActivatedBonusState = useCallback(activatedBonusData => {
    const nextBonus = activatedBonusData?.disabled ? null : activatedBonusData;

    setSelectedBonus(nextBonus);
    setIsBonusesDisabled(true);
    intendedBonusRef.current = nextBonus;
  }, []);
  const keepBonusAsIntention = useCallback(() => {
    setSelectedBonus(currentSelectedBonus => {
      if (!currentSelectedBonus) {
        return currentSelectedBonus;
      }

      intendedBonusRef.current = currentSelectedBonus;

      return null;
    });
  }, []);
  const restoreIntendedBonus = useCallback(() => {
    if (!intendedBonusRef.current) {
      return;
    }

    setSelectedBonus(intendedBonusRef.current);
    intendedBonusRef.current = null;
  }, []);
  const rememberCompletedDepositBonus = useCallback(
    bonus => {
      intendedBonusRef.current = bonus;
      setSelectedBonus(bonus);
      setDepositCompleteBonus(bonus);
      setDepositCompletePaymentMethodId(paymentMethod?.id ?? null);
      setHasPaymentMethodChangedSinceDepositComplete(false);
      setIsBonusesDisabled(true);
    },
    [paymentMethod?.id],
  );
  const isBonusAllowed = useCallback(
    bonus => {
      const normalizeBonus = bonus?.bonus ? bonus.bonus : bonus;

      const { notAllowedCountries = [], notAllowedPaymentMethods = [] } = normalizeBonus.basicLimit;

      return (
        !notAllowedPaymentMethods.includes(paymentMethod?.id?.toString()) &&
        !notAllowedCountries.includes(country?.toLowerCase())
      );
    },
    [country, paymentMethod?.id],
  );
  const normalizeBonus = useCallback(
    bonus => {
      if (!bonus) {
        return null;
      }

      const normalizedBonus = normalizeBonusWithFinanceData(bonus, currency);

      return {
        ...normalizedBonus,
        disabled: !isBonusAllowed(normalizedBonus),
      };
    },
    [currency, isBonusAllowed],
  );

  const depositBonuses = useMemo(() => {
    if (!paymentMethod?.id) {
      return [];
    }

    const activeDepositBonuses = activeBonuses
      .filter(bonus => {
        const { bonus: normalizedBonus = {}, status } = bonus;
        return status === ACTIVATED && isCasinoDeposit(normalizedBonus);
      })
      .map(bonus => ({
        ...normalizeBonusWithFinanceData(bonus, currency),
        disabled: !isBonusAllowed(bonus),
      }))
      .sort(sortBonusesByAvailabilityAndAmount);

    const availableDepositBonuses = bonuses
      .filter(isCasinoDeposit)
      .map(bonus => ({
        ...normalizeBonusWithFinanceData(bonus, currency),
        disabled: !isBonusAllowed(bonus),
      }))
      .sort(sortBonusesByAvailabilityAndAmount);

    return [...activeDepositBonuses, ...availableDepositBonuses];
  }, [activeBonuses, bonuses, currency, isBonusAllowed, paymentMethod?.id]);

  const typesToHide = useMemo(() => {
    return activeBonuses
      .filter(
        active =>
          active.status === IN_PROGRESS &&
          (active.bonus?.bonusType === NO_DEPOSIT ||
            active.bonus?.bonusType === RELOAD_NO_DEPOSIT ||
            active.bonus?.productType === CASINO_PRODUCT_TYPE ||
            active.bonus?.productType === SPORTSBOOK_PRODUCT_TYPE),
      )
      .map(active => active.bonus?.productType);
  }, [activeBonuses]);

  const depositBonusesToShow = useMemo(() => {
    const filteredBonuses = typesToHide.length
      ? depositBonuses.filter(bonus => !typesToHide.includes(bonus.productType))
      : depositBonuses;

    return orderBonusesByAvailability(filteredBonuses);
  }, [depositBonuses, typesToHide]);

  const internalActivatedBonus = useMemo(() => {
    const latestActivatedBonus = activeBonuses
      .filter(({ bonus: normalizedBonus = {}, status }) => {
        return status === ACTIVATED && isCasinoDeposit(normalizedBonus);
      })
      .reduce((latestBonus, currentBonus) => {
        if (!latestBonus) return currentBonus;

        return getActivationSortValue(currentBonus) >= getActivationSortValue(latestBonus)
          ? currentBonus
          : latestBonus;
      }, null);

    if (!latestActivatedBonus) {
      return null;
    }

    return normalizeBonus(latestActivatedBonus);
  }, [activeBonuses, normalizeBonus]);

  const hasActivatedSportsbookBonus = useMemo(() => {
    return activeBonuses.some(({ bonus, status }) => {
      return (
        status === ACTIVATED &&
        checkBonusTypeFits(bonus?.bonusType) &&
        bonus?.productType === SPORTSBOOK_PRODUCT_TYPE
      );
    });
  }, [activeBonuses]);
  const isBonusBlockHidden =
    hasActivatedSportsbookBonus ||
    typesToHide.includes(CASINO_PRODUCT_TYPE);

  useEffect(() => {
    const handleDepositComplete = completedBonusData => {
      const completedBonus =
        completedBonusData || internalActivatedBonus || selectedBonus || intendedBonusRef.current;

      if (!completedBonus) {
        return;
      }

      rememberCompletedDepositBonus(completedBonus);
    };

    const findBonusByTransportPayload = ({ bonusId, bonusInstanceId }) => {
      const matchedActiveBonus = activeBonuses.find(({ bonus, id }) => {
        return isCasinoDeposit(bonus) && (id === bonusInstanceId || bonus?.id === bonusId);
      });

      if (matchedActiveBonus) {
        return normalizeBonus(matchedActiveBonus);
      }

      const matchedAvailableBonus = bonuses.find(bonus => {
        return isCasinoDeposit(bonus) && bonus.id === bonusId;
      });

      return normalizeBonus(matchedAvailableBonus);
    };

    const unsubscribe = TransportManager.getInstance().subscribe(
      {
        notificationTopic: NotificationTopic.BONUS_INSTANCE_STATUS_CHANGED,
      },
      data => {
        const completedBonus = findBonusByTransportPayload(data);

        handleDepositComplete(completedBonus);
      },
    );

    return unsubscribe;
  }, [
    activeBonuses,
    bonuses,
    internalActivatedBonus,
    normalizeBonus,
    rememberCompletedDepositBonus,
    selectedBonus,
  ]);

  useEffect(() => {
    if (
      depositCompletePaymentMethodId == null ||
      hasPaymentMethodChangedSinceDepositComplete ||
      paymentMethod?.id == null
    ) {
      return;
    }

    if (depositCompletePaymentMethodId !== paymentMethod.id) {
      setHasPaymentMethodChangedSinceDepositComplete(true);
    }
  }, [
    depositCompletePaymentMethodId,
    hasPaymentMethodChangedSinceDepositComplete,
    paymentMethod?.id,
  ]);

  useEffect(() => {
    if (internalActivatedBonus) {
      resetCompletedDepositState();
    }
  }, [internalActivatedBonus, resetCompletedDepositState]);

  useEffect(() => {
    if (internalActivatedBonus) {
      applyActivatedBonusState(internalActivatedBonus);
      return;
    }

    if (depositCompleteBonus) {
      setSelectedBonus(depositCompleteBonus);
      setIsBonusesDisabled(true);
      intendedBonusRef.current = depositCompleteBonus;
      return;
    }

    if (isBonusBlockHidden) {
      intendedBonusRef.current = null;
      setSelectedBonus(null);
      setIsBonusesDisabled(true);
      return;
    }

    const firstEnabledBonus = depositBonusesToShow.find(bonus => !bonus.disabled) || null;
    const findEnabledBonusById = bonus =>
      depositBonusesToShow.find(item => item.id === bonus?.id && !item.disabled) || null;

    if (intendedBonusRef.current && !findEnabledBonusById(intendedBonusRef.current)) {
      intendedBonusRef.current = null;
    }

    setSelectedBonus(prev => {
      if (prev) {
        return findEnabledBonusById(prev) || firstEnabledBonus;
      }

      if (intendedBonusRef.current || !firstEnabledBonus) {
        return null;
      }

      return firstEnabledBonus;
    });
  }, [
    applyActivatedBonusState,
    depositBonusesToShow,
    depositCompleteBonus,
    internalActivatedBonus,
    isBonusBlockHidden,
  ]);

  useEffect(() => {
    if (depositCompleteBonus) {
      setIsBonusesDisabled(true);
    }
  }, [depositCompleteBonus]);

  useEffect(() => {
    if (internalActivatedBonus || depositCompleteBonus) return;

    const bonusToCheck = intendedBonusRef.current || selectedBonus;

    if (!bonusToCheck) return;

    if (depositAmount === '') {
      setIsBonusesDisabled(false);

      return;
    }

    const currentAmount = Number(depositAmount);
    const requiredAmount = getMinDeposit(bonusToCheck);

    if (currentAmount >= requiredAmount) {
      restoreIntendedBonus();
      setIsBonusesDisabled(false);
      return;
    }

    if (currentAmount > 0) {
      keepBonusAsIntention();
      setIsBonusesDisabled(true);
    }
  }, [
    depositAmount,
    depositCompleteBonus,
    getMinDeposit,
    internalActivatedBonus,
    keepBonusAsIntention,
    restoreIntendedBonus,
    selectedBonus,
  ]);

  const handleSetSelectedBonus = useCallback(
    bonus => {
      if (!hasPaymentMethodChangedSinceDepositComplete) {
        resetCompletedDepositState();
      }

      if (!bonus) {
        intendedBonusRef.current = null;
        setSelectedBonus(null);

        return;
      }

      if (isBonusesDisabled) {
        intendedBonusRef.current = bonus;
        setSelectedBonus(null);
      } else {
        intendedBonusRef.current = null;
        setSelectedBonus(bonus);
      }
    },
    [hasPaymentMethodChangedSinceDepositComplete, isBonusesDisabled, resetCompletedDepositState],
  );

  const activatedBonus = internalActivatedBonus;

  const amountToGetBonus = useMemo(() => {
    const amount = Number(depositAmount);

    if (!depositAmount || Number.isNaN(amount)) return 0;

    const bonus = internalActivatedBonus || selectedBonus || intendedBonusRef.current;

    if (!bonus) return 0;

    const minDeposit = getMinDeposit(bonus);

    return Math.max(minDeposit - amount, 0);
  }, [depositAmount, internalActivatedBonus, getMinDeposit, selectedBonus]);

  const showBonus =
    !isBonusBlockHidden;

  const bonusesList = useMemo(() => {
    if (internalActivatedBonus) return [internalActivatedBonus];
    if (depositCompleteBonus) return [depositCompleteBonus];

    return depositBonusesToShow;
  }, [depositBonusesToShow, depositCompleteBonus, internalActivatedBonus]);

  const exposedDepositBonuses = useMemo(() => {
    if (internalActivatedBonus) {
      return [internalActivatedBonus];
    }

    if (depositCompleteBonus) {
      return [depositCompleteBonus];
    }

    return depositBonuses;
  }, [depositBonuses, depositCompleteBonus, internalActivatedBonus]);

  const contextValue = useMemo(
    () => ({
      activatedBonus,
      amountToGetBonus,
      bonusesList,
      depositBonuses: exposedDepositBonuses,
      inputValue: depositAmount,
      intendedBonusRef,
      isBonusesDisabled,
      paymentMethod,
      selectedBonus,
      setInputValue: setDepositAmount,
      setPaymentMethod,
      setSelectedBonus: handleSetSelectedBonus,
      showBonus,
    }),
    [
      activatedBonus,
      amountToGetBonus,
      bonusesList,
      exposedDepositBonuses,
      handleSetSelectedBonus,
      depositAmount,
      isBonusesDisabled,
      selectedBonus,
      showBonus,
      setPaymentMethod,
      paymentMethod,
    ],
  );

  return (
    <QuickDepositBonusesContext.Provider value={contextValue}>
      {children}
    </QuickDepositBonusesContext.Provider>
  );
};

QuickDepositBonusesProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
